"use client";
import Link from "next/link";
import { SignUpButton } from "@clerk/nextjs";
import { dsFontClass, dsTokensCss, Icon, Footer, type Lang } from "./svendetid-ds";
import LandingNav from "./landing-nav";

/* ---------------------------------------------------------------------- */
/* Copy                                                                    */
/* ---------------------------------------------------------------------- */

const T = {
  da: {
    metaTitle: "Automatisering",
    kicker: "Automatisering",
    h1a: "Rykkeren blev sendt ",
    h1b: "kl. 03:14",
    h1c: ". Du sov.",
    sub: "Svendetid holder øje med hvert tilbud og hver faktura, døgnet rundt. Når fristen udløber, går der besked — uden at du skal huske det, ringe op eller skrive en eneste mail.",
    cta: "Kom i gang – det er gratis",
    seePricing: "Se priser",
    trust1: "Gratis at komme i gang",
    trust2: "Ingen kort krævet",
    trust3: "Dansk support",

    logCaption: "Uddrag af automatiseringslog — natten mellem d. 11. og 12. juni",
    logEntries: [
      { time: "23:58", who: "system", text: "Tjekker 34 åbne tilbud og 12 udestående fakturaer for forfald." },
      { time: "00:02", who: "system", text: "Tilbud #T-108 (Madsen) — 3 dage uden svar. Sender påmindelse." },
      { time: "00:02", who: "sent", text: "→ Påmindelse sendt til madsen@mail.dk" },
      { time: "03:14", who: "system", text: "Faktura #2847 (Larsen) — forfalden siden d. 10. juni. Sender rykker 1 af 2." },
      { time: "03:14", who: "sent", text: "→ Rykker sendt til larsen@firma.dk" },
      { time: "06:40", who: "ai", text: "Svend AI: \"2 tilbud og 1 faktura krævede opfølgning i nat — begge sendt.\"" },
    ],

    // Without / With — presented as a ledger, not a two-card comparison
    cmpKicker: "Før og efter",
    cmpH2: "Det du plejede at gøre — og det Svendetid gør nu",
    without: [
      "Sætte en påmindelse i kalenderen, som du alligevel glemmer",
      "Ringe forsigtigt til en kunde der ikke har betalt",
      "Bruge søndag aften på at rydde op i tilbud der er gået i glemmebogen",
      "Opdage for sent at en regning er 40 dage forfalden",
    ],
    with: [
      "Systemet sætter selv en frist, den dag tilbuddet sendes",
      "En venlig, automatisk rykker — aldrig et akavet opkald",
      "Søndag aften er fri. Opfølgningen skete for længst",
      "Du ser status i realtid, længe før den bliver et problem",
    ],

    // Section intro
    introKicker: "Hvordan det virker",
    introH2: "To automatiske spor — ét overblik",
    introSub: "Tilbud der venter på svar. Fakturaer der er forfaldne. Systemet arbejder på begge, mens du er ude på sagen.",

    blocks: [
      {
        num: "01",
        label: "Tilbudspåmindelser",
        h3: "Tilbuddet sendes — påmindelsen sætter sig selv",
        body: "Så snart et tilbud går ud af døren, lægger Svendetid en frist ind bag kulisserne. Svarer kunden ikke i tide, sender systemet en rolig, velformuleret opfølgning på dine vegne — samme dag fristen rammer, ikke tre uger senere.",
        stat: "3 dage",
        statLabel: "typisk frist før auto-opfølgning",
        chips: ["Selvvalgt frist", "Skrives i din tone", "Ingen dobbelt-opfølgning"],
      },
      {
        num: "02",
        label: "Fakturarykker",
        h3: "Forfaldne fakturaer får deres egen tidslinje",
        body: "Betalingsfristen er ikke en anbefaling — det er en trigger. Falder en faktura om, sætter Svendetid en rykkerserie i gang efter dine regler: hvor mange, med hvilket mellemrum, og med hvilken tone. Du undgår den akavede opringning helt.",
        stat: "2×",
        statLabel: "rykkere før eskalering, som standard",
        chips: ["Egne intervaller", "Betalingsstatus i realtid", "Ingen manuel afsendelse"],
      },
      {
        num: "03",
        label: "Svend AI",
        h3: "Bed AI'en tale for dig — den spørger, før den handler",
        body: "\"Er der tilbud der mangler svar?\" — Svend AI finder dem på et øjeblik, foreslår hvad der skal ske, og venter på dit ja. Intet sendes uden godkendelse. Du bevarer fuld kontrol, men slipper for at holde det hele i hovedet.",
        stat: "1 svar",
        statLabel: "for at godkende hele køen på én gang",
        chips: ["Naturligt sprog", "Bekræfter altid først", "Samler flere opgaver i én"],
      },
    ],

    // AI conversation panel
    aiKicker: "Svend AI",
    aiH2a: "Spørg med almindelige ord. ",
    aiH2b: "Svend gør resten.",
    aiBody: "\"Send en rykker til alle forfaldne fakturaer\" eller \"Hvilke tilbud har ikke fået svar i over 5 dage?\" — Svend AI finder svaret, foreslår handlingen, og venter på dit ja.",
    aiCta: "Prøv Svend AI",
    aiQuestion: "Er der tilbud der mangler svar, og fakturaer der er forfaldne?",
    aiAnswer1: "Ja — 2 tilbud venter stadig:",
    aiOffer1: "#T-112 · Hansen · Badeværelse · kr 18.400",
    aiOffer2: "#T-108 · Madsen · Tagrender · kr 6.200",
    aiAnswer2: "Og 1 faktura er forfalden:",
    aiInv1: "#2847 · Larsen · kr 4.750 · forfalden 10. jun",
    aiOutro: "Skal jeg sende påmindelser og rykker nu?",
    aiConfirm: "Ja, send dem alle",

    // Feature grid — presented as a spec sheet
    gridKicker: "Detaljerne",
    gridH2: "Alt hvad automatiseringen dækker",
    grid: [
      { k: "01", title: "Auto-tilbudspåminder", body: "Kunder der tier, får en venlig opfølgning inden for den frist du selv sætter." },
      { k: "02", title: "Fakturarykker", body: "Forfaldne fakturaer opdages og rykkes efter din opsætning — ingen manuelle trin." },
      { k: "03", title: "Svend AI", body: "Bed Svend sende, følge op eller rykke. AI'en bekræfter, før den sender noget." },
      { k: "04", title: "Status-overblik", body: "Alle ubesvarede tilbud og ubetalte fakturaer, samlet ét sted, opdateret live." },
      { k: "05", title: "Tilpassede regler", body: "Vælg frister, antal rykkere og afsendelsestidspunkt. Dine regler, ikke systemets." },
      { k: "06", title: "Bekræftelse altid", body: "Intet sendes af AI uden dit samtykke. Du er i kontrol, hver gang." },
    ],

    // Platform
    platKicker: "Én platform",
    platH2: "Automatisering er limen, ikke et tillægsmodul",
    platSub: "Tilbud, jobs, timer, fakturascanning og fakturering lever i samme system. Automatiseringen binder dem sammen, i baggrunden.",
    platform: [
      { title: "Tilbud", sub: "Automatisk AI-tilbud", href: "/features/offer" },
      { title: "Fakturering", sub: "Fra timer til faktura", href: "/features" },
      { title: "Sagsstyring", sub: "Styr hvert job", href: "/features" },
      { title: "Rapporter", sub: "Overblik der virker", href: "/features/reports" },
      { title: "Kundekartotek", sub: "Søg og kontaktkort", href: "/features/customer-database" },
      { title: "Alle funktioner", sub: "Se hele platformen", href: "/features" },
    ],

    secKicker: "Sikkerhed",
    security: ["GDPR-overholdt", "Data hostet i EU", "Support på dansk"],

    faqKicker: "Spørgsmål",
    faqH2: "Ofte stillede spørgsmål",
    faqs: [
      { q: "Hvornår sender systemet en tilbudspåmindelse?", a: "Du vælger en frist — f.eks. 3 dage. Svarer kunden ikke inden for den tid, sender Svendetid automatisk en venlig opfølgningsmail på dine vegne. Du behøver ikke gøre noget." },
      { q: "Kan jeg sætte op hvor mange rykkere der sendes?", a: "Ja. Du vælger selv antal rykkere og intervallet mellem dem — f.eks. rykker 1 efter 3 dage, rykker 2 efter 7 dage. Alt tilpasses din arbejdsgang." },
      { q: "Hvad gør Svend AI anderledes end de automatiske regler?", a: "Svend AI reagerer på naturligt sprog. Du kan spørge den »Hvem skylder mig penge?« eller sige »Send en rykker til Hansen nu« — AI'en finder svaret og handler på din kommando." },
      { q: "Sender Svend AI uden min godkendelse?", a: "Nej. Svend AI bekræfter altid, inden der sendes noget. Du godkender med ét svar, og AI'en udfører. Du er altid i kontrol." },
      { q: "Fungerer det med mine eksisterende tilbud og fakturaer?", a: "Ja. Automatiseringen gælder alle tilbud og fakturaer i Svendetid — både nye og eksisterende. Du sætter reglerne op én gang, og systemet klarer resten." },
      { q: "Er det inkluderet i den gratis plan?", a: "Grundlæggende påmindelser er tilgængelige på gratis planen. Avancerede regler og Svend AI-automatisering er en del af de betalte planer." },
    ],

    bandKicker: "Klar til at slippe rykkerne?",
    bandH2: "Lad systemet huske det, du ikke har tid til",
    bandBody: "Tilbudspåmindelser og fakturarykker, sendt automatisk. Svend AI klarer resten, når du beder om det. Intet betalingskort krævet.",
  },
  en: {
    metaTitle: "Automation",
    kicker: "Automation",
    h1a: "The reminder went out at ",
    h1b: "3:14 AM",
    h1c: ". You were asleep.",
    sub: "Svendetid watches every offer and every invoice, around the clock. When a deadline passes, something happens — without you remembering, calling, or writing a single email.",
    cta: "Get started – it's free",
    seePricing: "See pricing",
    trust1: "Free to get started",
    trust2: "No card required",
    trust3: "Danish support",

    logCaption: "Excerpt from the automation log — overnight, June 11–12",
    logEntries: [
      { time: "23:58", who: "system", text: "Checking 34 open offers and 12 outstanding invoices for deadlines." },
      { time: "00:02", who: "system", text: "Offer #T-108 (Madsen) — 3 days without reply. Sending reminder." },
      { time: "00:02", who: "sent", text: "→ Reminder sent to madsen@mail.dk" },
      { time: "03:14", who: "system", text: "Invoice #2847 (Larsen) — overdue since June 10. Sending reminder 1 of 2." },
      { time: "03:14", who: "sent", text: "→ Reminder sent to larsen@company.dk" },
      { time: "06:40", who: "ai", text: "Svend AI: \"2 offers and 1 invoice needed follow-up overnight — both sent.\"" },
    ],

    cmpKicker: "Before and after",
    cmpH2: "What you used to do — and what Svendetid does now",
    without: [
      "Set a reminder in your calendar you forget about anyway",
      "Awkwardly call a customer who hasn't paid",
      "Spend Sunday evening cleaning up offers that fell through the cracks",
      "Notice too late that an invoice is 40 days overdue",
    ],
    with: [
      "The system sets a deadline the day the offer goes out",
      "A friendly, automatic reminder — never an awkward call",
      "Sunday evening is free. The follow-up happened long ago",
      "You see status in real time, long before it becomes a problem",
    ],

    introKicker: "How it works",
    introH2: "Two automatic tracks — one overview",
    introSub: "Offers waiting for a reply. Invoices past due. The system works on both while you're out on the job.",

    blocks: [
      {
        num: "01",
        label: "Offer reminders",
        h3: "The offer goes out — the reminder sets itself",
        body: "The moment an offer leaves your outbox, Svendetid quietly sets a deadline behind the scenes. If the customer doesn't respond in time, the system sends a calm, well-written follow-up on your behalf — the day the deadline hits, not three weeks later.",
        stat: "3 days",
        statLabel: "typical deadline before auto follow-up",
        chips: ["Custom deadline", "Written in your tone", "No duplicate follow-ups"],
      },
      {
        num: "02",
        label: "Invoice reminders",
        h3: "Overdue invoices get their own timeline",
        body: "A payment deadline isn't a suggestion — it's a trigger. When an invoice falls overdue, Svendetid starts a reminder sequence by your rules: how many, how far apart, and in what tone. You skip the awkward phone call entirely.",
        stat: "2×",
        statLabel: "reminders before escalation, by default",
        chips: ["Custom intervals", "Live payment status", "No manual sending"],
      },
      {
        num: "03",
        label: "Svend AI",
        h3: "Ask the AI to speak for you — it checks first",
        body: "\"Are there offers waiting for a reply?\" — Svend AI finds them instantly, proposes what should happen, and waits for your yes. Nothing is sent without approval. You keep full control without carrying it all in your head.",
        stat: "1 reply",
        statLabel: "to approve the whole queue at once",
        chips: ["Natural language", "Always confirms first", "Bundles multiple tasks"],
      },
    ],

    aiKicker: "Svend AI",
    aiH2a: "Ask in plain language. ",
    aiH2b: "Svend does the rest.",
    aiBody: "\"Send a reminder to all overdue invoices\" or \"Which offers haven't been answered in over 5 days?\" — Svend AI finds the answer, proposes the action, and waits for your yes.",
    aiCta: "Try Svend AI",
    aiQuestion: "Are there offers waiting for a reply, and invoices that are overdue?",
    aiAnswer1: "Yes — 2 offers still waiting:",
    aiOffer1: "#T-112 · Hansen · Bathroom · kr 18,400",
    aiOffer2: "#T-108 · Madsen · Gutters · kr 6,200",
    aiAnswer2: "And 1 invoice is overdue:",
    aiInv1: "#2847 · Larsen · kr 4,750 · overdue Jun 10",
    aiOutro: "Shall I send reminders and the chase now?",
    aiConfirm: "Yes, send them all",

    gridKicker: "The details",
    gridH2: "Everything the automation covers",
    grid: [
      { k: "01", title: "Auto offer reminder", body: "Customers who go quiet get a friendly follow-up within the deadline you set." },
      { k: "02", title: "Invoice reminders", body: "Overdue invoices are detected and chased on your schedule — no manual steps." },
      { k: "03", title: "Svend AI", body: "Ask Svend to send, follow up, or chase. The AI confirms before it sends anything." },
      { k: "04", title: "Status overview", body: "Every unanswered offer and unpaid invoice, in one place, updated live." },
      { k: "05", title: "Custom rules", body: "Choose deadlines, number of reminders, and send time. Your rules, not the system's." },
      { k: "06", title: "Always confirmed", body: "The AI never sends without your consent. You're in control, every time." },
    ],

    platKicker: "One platform",
    platH2: "Automation is the glue, not an add-on",
    platSub: "Offers, jobs, hours, invoice scanning and invoicing live in one system. Automation ties them together, in the background.",
    platform: [
      { title: "Offers", sub: "Automatic AI offers", href: "/en/features/offer" },
      { title: "Invoicing", sub: "From hours to invoice", href: "/en/features" },
      { title: "Job management", sub: "Run every job", href: "/en/features" },
      { title: "Reports", sub: "Overview that works", href: "/en/features/reports" },
      { title: "Customer database", sub: "Search and contact cards", href: "/en/features/customer-database" },
      { title: "All features", sub: "See the whole platform", href: "/en/features" },
    ],

    secKicker: "Security",
    security: ["GDPR compliant", "Data hosted in the EU", "Support in Danish"],

    faqKicker: "Questions",
    faqH2: "Frequently asked questions",
    faqs: [
      { q: "When does the system send an offer reminder?", a: "You choose a deadline — e.g. 3 days. If the customer doesn't reply within that time, Svendetid automatically sends a friendly follow-up email on your behalf. You don't need to do anything." },
      { q: "Can I set how many reminders are sent?", a: "Yes. You choose the number of reminders and the interval between them — e.g. reminder 1 after 3 days, reminder 2 after 7 days. Everything adapts to your workflow." },
      { q: "What does Svend AI do differently from the automatic rules?", a: "Svend AI responds to natural language. You can ask it \"Who owes me money?\" or say \"Send a reminder to Hansen now\" — the AI finds the answer and acts on your command." },
      { q: "Does Svend AI send without my approval?", a: "No. Svend AI always confirms before anything is sent. You approve with one reply, and the AI executes. You are always in control." },
      { q: "Does it work with my existing offers and invoices?", a: "Yes. Automation applies to all offers and invoices in Svendetid — both new and existing. You set the rules once, and the system handles the rest." },
      { q: "Is it included in the free plan?", a: "Basic reminders are available on the free plan. Advanced rules and Svend AI automation are part of the paid plans." },
    ],

    bandKicker: "Ready to stop chasing?",
    bandH2: "Let the system remember what you don't have time to",
    bandBody: "Offer reminders and invoice chases, sent automatically. Svend AI handles the rest, when you ask. No payment card required.",
  },
} as const;

type TT = (typeof T)["da"] | (typeof T)["en"];

/* ---------------------------------------------------------------------- */
/* Local-only CSS — animations and structural rules dsTokensCss doesn't   */
/* cover. All colors reference the shared svendetid-ds custom properties. */
/* ---------------------------------------------------------------------- */

const CSS = `
.auto-faq summary::-webkit-details-marker { display: none; }
.auto-faq summary { cursor: pointer; list-style: none; }
.auto-faq .auto-faq-plus { transition: transform .2s ease; }
.auto-faq[open] .auto-faq-plus { transform: rotate(45deg); }

@keyframes auto-blink { 0%, 45% { opacity: 1; } 50%, 95% { opacity: 0; } 100% { opacity: 1; } }
.auto-cursor { animation: auto-blink 1.1s steps(1) infinite; }

@keyframes auto-rise {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.auto-log-row { animation: auto-rise .5s ease both; }

@media (prefers-reduced-motion: reduce) {
  .auto-cursor { animation: none; opacity: 1; }
  .auto-log-row { animation: none; }
}

@media (max-width: 860px) {
  .auto-block-grid { grid-template-columns: 1fr !important; }
  .auto-block-visual { order: -1 !important; }
}
`;

/* ---------------------------------------------------------------------- */
/* Small pieces                                                           */
/* ---------------------------------------------------------------------- */

function SectionEyebrow({ label, center }: { label: string; center?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 14, justifyContent: center ? "center" : undefined }}>
      {center && <div style={{ flex: 1, height: 1, background: "var(--border)", maxWidth: 60 }} />}
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--accent-strong)" }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: "var(--border)", maxWidth: center ? 60 : undefined }} />
    </div>
  );
}

/* Terminal-style log excerpt used in the hero — dark chrome matches the */
/* phone-mockup frames used elsewhere in the system (#0A100D / #2C3B35). */
function AutomationLog({ t }: { t: TT }) {
  const colorFor = (who: string) =>
    who === "sent" ? "#7FC8A0" : who === "ai" ? "#F4C8A6" : "var(--text-hero-muted)";
  return (
    <div style={{ background: "#0A100D", borderRadius: 14, border: "1px solid #2C3B35", overflow: "hidden", boxShadow: "0 30px 70px rgba(0,0,0,0.45)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 18px", borderBottom: "1px solid #2C3B35" }}>
        <span style={{ width: 9, height: 9, borderRadius: "50%", background: "var(--accent)" }} />
        <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#5B6963" }} />
        <span style={{ width: 9, height: 9, borderRadius: "50%", background: "var(--brand)" }} />
        <span style={{ marginLeft: 8, fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-hero-muted)" }}>automation.log</span>
      </div>
      <div style={{ padding: "18px 20px 22px" }}>
        {t.logEntries.map((e, i) => (
          <div key={i} className="auto-log-row" style={{ animationDelay: `${i * 90}ms`, display: "flex", gap: 14, padding: "7px 0", fontSize: 13, lineHeight: 1.55 }}>
            <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-hero-muted)", flex: "none", width: 46 }}>{e.time}</span>
            <span style={{ fontFamily: "var(--font-mono)", color: colorFor(e.who) }}>{e.text}</span>
          </div>
        ))}
        <div className="auto-log-row" style={{ animationDelay: `${t.logEntries.length * 90}ms`, display: "flex", gap: 14, padding: "7px 0", fontSize: 13 }}>
          <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-hero-muted)", flex: "none", width: 46 }}>—</span>
          <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-hero-muted)" }}>_<span className="auto-cursor">▌</span></span>
        </div>
      </div>
    </div>
  );
}

/* AI chat panel — a light card floating inside the dark AI section,     */
/* styled like a receipt/annotation rather than a bubble chat.           */
function AiPanel({ t }: { t: TT }) {
  return (
    <div style={{ background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 16, padding: "24px 24px 22px", position: "relative", boxShadow: "0 24px 60px rgba(16,33,27,0.22)" }}>
      <div aria-hidden style={{ position: "absolute", top: 16, right: 18, fontSize: 11, letterSpacing: "0.08em", fontFamily: "var(--font-mono)", color: "var(--text-muted)" }}>SVEND · AI</div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--text-muted)", marginBottom: 14, paddingBottom: 14, borderBottom: "1px dashed var(--hairline)" }}>
        <span style={{ color: "var(--text-muted)" }}>&gt;&nbsp;</span>{t.aiQuestion}
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.7, color: "var(--text-body)" }}>
        <p style={{ margin: "0 0 8px" }}>{t.aiAnswer1}</p>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--text-muted)", paddingLeft: 14, borderLeft: "2px solid var(--orange-200)", marginBottom: 4 }}>{t.aiOffer1}</div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--text-muted)", paddingLeft: 14, borderLeft: "2px solid var(--orange-200)", marginBottom: 12 }}>{t.aiOffer2}</div>
        <p style={{ margin: "0 0 8px" }}>{t.aiAnswer2}</p>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--text-muted)", paddingLeft: 14, borderLeft: "2px solid var(--tint-mint)", marginBottom: 14 }}>{t.aiInv1}</div>
        <p style={{ margin: "0 0 12px", color: "var(--text-muted)" }}>{t.aiOutro}</p>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "#fff", background: "var(--brand)", padding: "9px 14px", borderRadius: 8 }}>
          <Icon name="checkmark" size={14} color="#fff" /> {t.aiConfirm}
        </div>
      </div>
    </div>
  );
}

function FeatureBlock({ block, t }: { block: TT["blocks"][number]; t: TT }) {
  return (
    <section style={{ maxWidth: 1180, margin: "0 auto", padding: "56px 32px" }}>
      <div className="auto-block-grid" style={{ display: "grid", gridTemplateColumns: "0.85fr 1.15fr", gap: 56, alignItems: "center" }}>
        <div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 14, marginBottom: 18 }}>
            <span style={{ fontFamily: "var(--font-serif)", fontSize: 15, color: "var(--text-muted)", fontStyle: "italic" }}>{block.num}</span>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--accent-strong)" }}>{block.label}</span>
          </div>
          <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 28, lineHeight: 1.18, letterSpacing: "-0.01em", margin: 0, color: "var(--green-900)" }}>{block.h3}</h3>
          <p style={{ fontSize: 16, color: "var(--text-muted)", lineHeight: 1.65, margin: "18px 0 0", maxWidth: "48ch" }}>{block.body}</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 22 }}>
            {block.chips.map((c) => (
              <span key={c} style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: 6, padding: "5px 10px" }}>{c}</span>
            ))}
          </div>
        </div>
        <div className="auto-block-visual" style={{ border: "1px solid var(--border)", borderRadius: 16, padding: "34px 30px", background: "var(--sage-50)", position: "relative" }}>
          <div aria-hidden style={{ position: "absolute", inset: 0, opacity: 0.5, backgroundImage: "radial-gradient(var(--sage-300) 0.6px, transparent 0.6px)", backgroundSize: "14px 14px", pointerEvents: "none" }} />
          <div style={{ position: "relative", display: "flex", alignItems: "baseline", gap: 12 }}>
            <span style={{ fontFamily: "var(--font-serif)", fontSize: 56, fontWeight: 600, lineHeight: 1, color: "var(--accent-strong)" }}>{block.stat}</span>
          </div>
          <div style={{ position: "relative", fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--text-muted)", marginTop: 10, maxWidth: "24ch" }}>{block.statLabel}</div>
        </div>
      </div>
    </section>
  );
}

function FaqItem({ q, a, defaultOpen }: { q: string; a: string; defaultOpen?: boolean }) {
  return (
    <details className="auto-faq" open={defaultOpen} style={{ borderBottom: "1px solid var(--hairline)", padding: "20px 0" }}>
      <summary style={{ display: "flex", alignItems: "center", gap: 18 }}>
        <span style={{ flex: 1, fontSize: 17, fontWeight: 500, fontFamily: "var(--font-serif)", color: "var(--green-900)" }}>{q}</span>
        <span className="auto-faq-plus" style={{ fontFamily: "var(--font-mono)", fontSize: 20, color: "var(--accent-strong)", flex: "none" }}>+</span>
      </summary>
      <p style={{ fontSize: 15, lineHeight: 1.65, color: "var(--text-muted)", margin: "14px 0 0", maxWidth: "68ch" }}>{a}</p>
    </details>
  );
}

/* ---------------------------------------------------------------------- */
/* Page                                                                    */
/* ---------------------------------------------------------------------- */

export default function AutomationContent({ lang = "da" }: { lang?: Lang }) {
  const t = T[lang];
  const pricingHref = lang === "en" ? "/en/pricing" : "/pricing";
  const aiHref = lang === "en" ? "/en/ai" : "/ai";
  const baseUrl = "https://www.svendetid.dk";
  const pageUrl = lang === "en" ? `${baseUrl}/en/features/automation` : `${baseUrl}/features/automation`;
  const inLanguage = lang === "en" ? "en-US" : "da-DK";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: lang === "en" ? "Home" : "Forside", item: lang === "en" ? `${baseUrl}/en` : baseUrl },
          { "@type": "ListItem", position: 2, name: lang === "en" ? "Features" : "Funktioner", item: lang === "en" ? `${baseUrl}/en/features` : `${baseUrl}/features` },
          { "@type": "ListItem", position: 3, name: t.metaTitle, item: pageUrl },
        ],
      },
      {
        "@type": "Service",
        "@id": `${pageUrl}#service`,
        name: lang === "en" ? "Svendetid Automation" : "Svendetid Automatisering",
        url: pageUrl,
        inLanguage,
        serviceType: lang === "en" ? "Offer reminders and invoice payment reminders" : "Tilbudspåmindelser og fakturarykker",
        provider: { "@type": "Organization", name: "Svendetid", url: baseUrl },
        description:
          lang === "en"
            ? "Automated offer reminders and overdue invoice chasers, plus an AI assistant that finds and follows up on what needs attention."
            : "Automatiske tilbudspåmindelser og rykkere for forfaldne fakturaer, samt en AI-assistent der finder og følger op på det, der kræver opmærksomhed.",
      },
      {
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        inLanguage,
        mainEntity: t.faqs.map(({ q, a }) => ({
          "@type": "Question",
          name: q,
          acceptedAnswer: { "@type": "Answer", text: a },
        })),
      },
    ],
  };

  return (
    <div className={`svendetid-ds ${dsFontClass}`} style={{ background: "#FFFFFF" }}>
      <style>{dsTokensCss}</style>
      <style>{CSS}</style>
      <style>{`html,body{background:#FFFFFF;overscroll-behavior-y:none;overflow-x:hidden;overflow-x:clip;}`}</style>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <LandingNav lang={lang} accent="green" />

      {/* HERO — dark green-900, matches /features and /pricing hero shape */}
      <section style={{ background: "var(--green-900)", color: "var(--text-on-hero)", position: "relative", overflow: "hidden", paddingTop: 64 }}>
        <div className="ds-2col ds-hero" style={{ maxWidth: 1180, margin: "0 auto", padding: "88px 32px 96px", display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: 56, alignItems: "center" }}>
          <div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#F4C8A6" }}>{t.kicker}</div>
            <h1 className="ds-h1" style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 54, lineHeight: 1.06, letterSpacing: "-0.02em", margin: "18px 0 0", color: "#fff", textWrap: "balance" }}>
              {t.h1a}<span style={{ fontStyle: "italic", color: "#F4C8A6" }}>{t.h1b}</span>{t.h1c}
            </h1>
            <p style={{ fontSize: 18, lineHeight: 1.6, color: "var(--text-hero-muted)", maxWidth: "30em", margin: "22px 0 0" }}>{t.sub}</p>
            <div className="ds-cta-row" style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 34 }}>
              <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.cta}<Icon name="arrow-forward" size={18} /></button></SignUpButton>
              <Link href={pricingHref} className="btn-ghost-dark" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 22px", borderRadius: 11, border: "1px solid rgba(255,255,255,0.24)" }}>{t.seePricing}</Link>
            </div>
            <div style={{ display: "flex", gap: 22, flexWrap: "wrap", marginTop: 26, fontSize: 13, fontFamily: "var(--font-mono)" }}>
              <span style={{ color: "var(--text-hero-muted)" }}>· {t.trust1}</span>
              <span style={{ color: "var(--text-hero-muted)" }}>· {t.trust2}</span>
              <span style={{ color: "var(--text-hero-muted)" }}>· {t.trust3}</span>
            </div>
          </div>
          <div>
            <AutomationLog t={t} />
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--text-hero-muted)", marginTop: 12, textAlign: "right" }}>{t.logCaption}</div>
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER — ledger style */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "72px 32px 56px" }}>
        <SectionEyebrow label={t.cmpKicker} />
        <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 34, lineHeight: 1.15, letterSpacing: "-0.015em", margin: "16px 0 40px", color: "var(--green-900)", maxWidth: "22ch" }}>{t.cmpH2}</h2>
        <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, border: "1px solid var(--border)", borderRadius: 16, overflow: "hidden" }}>
          <div style={{ padding: "8px 0" }}>
            {t.without.map((item, i) => (
              <div key={item} style={{ display: "flex", gap: 16, padding: "16px 26px", borderBottom: i < t.without.length - 1 ? "1px solid var(--hairline)" : "none" }}>
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-muted)", flex: "none" }}>✕</span>
                <span style={{ fontSize: 15, color: "var(--text-muted)", lineHeight: 1.55, textDecoration: "line-through", textDecorationColor: "var(--border)" }}>{item}</span>
              </div>
            ))}
          </div>
          <div style={{ padding: "8px 0", background: "var(--sage-50)", borderLeft: "1px solid var(--border)" }}>
            {t.with.map((item, i) => (
              <div key={item} style={{ display: "flex", gap: 16, padding: "16px 26px", borderBottom: i < t.with.length - 1 ? "1px solid var(--hairline)" : "none" }}>
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--brand)", flex: "none" }}>✓</span>
                <span style={{ fontSize: 15, color: "var(--text-body)", lineHeight: 1.55 }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION INTRO */}
      <section style={{ maxWidth: 780, margin: "0 auto", padding: "40px 32px 0", textAlign: "center" }}>
        <SectionEyebrow label={t.introKicker} center />
        <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 36, lineHeight: 1.15, letterSpacing: "-0.015em", margin: "18px 0 0", color: "var(--green-900)" }}>{t.introH2}</h2>
        <p style={{ fontSize: 17, color: "var(--text-muted)", margin: "14px auto 0", maxWidth: "48ch" }}>{t.introSub}</p>
      </section>

      {/* THREE FEATURE BLOCKS */}
      {t.blocks.map((block) => (
        <FeatureBlock key={block.label} block={block} t={t} />
      ))}

      {/* AI PANEL — echoes the /features "Quick-start quotes" dark panel */}
      <section style={{ padding: "40px 32px 64px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", background: "var(--green-900)", borderRadius: 24, padding: "clamp(32px,5vw,60px)", boxShadow: "0 24px 60px rgba(16,33,27,0.22)" }}>
          <div className="ds-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 52, alignItems: "center" }}>
            <div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 13px", borderRadius: 999, background: "rgba(249,115,22,0.16)", border: "1px solid rgba(249,115,22,0.35)" }}>
                <Icon name="sparkles" size={14} color="#F4C8A6" />
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "#F4C8A6" }}>{t.aiKicker}</span>
              </div>
              <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 34, lineHeight: 1.16, letterSpacing: "-0.02em", margin: "18px 0 0", color: "#fff" }}>
                {t.aiH2a}<span style={{ fontStyle: "italic", color: "#F4C8A6" }}>{t.aiH2b}</span>
              </h2>
              <p style={{ fontSize: 16, lineHeight: 1.65, color: "var(--text-hero-muted)", margin: "18px 0 0", maxWidth: "42ch" }}>{t.aiBody}</p>
              <div style={{ marginTop: 28 }}>
                <Link href={aiHref} className="btn-blue" style={{ textDecoration: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.aiCta}<Icon name="arrow-forward" size={18} /></Link>
              </div>
            </div>
            <AiPanel t={t} />
          </div>
        </div>
      </section>

      {/* SPEC SHEET / FEATURE GRID */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "40px 32px 56px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16, marginBottom: 28 }}>
          <div>
            <SectionEyebrow label={t.gridKicker} />
            <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 32, lineHeight: 1.15, letterSpacing: "-0.015em", margin: "16px 0 0", color: "var(--green-900)" }}>{t.gridH2}</h2>
          </div>
        </div>
        <div className="ds-3col" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", border: "1px solid var(--border)", borderRadius: 16, overflow: "hidden" }}>
          {t.grid.map((f, i) => (
            <div key={f.title} style={{
              padding: "26px 24px",
              borderRight: (i % 3 !== 2) ? "1px solid var(--hairline)" : "none",
              borderBottom: i < t.grid.length - 3 ? "1px solid var(--hairline)" : "none",
              background: i % 2 === 0 ? "var(--surface-card)" : "var(--sage-50)",
            }}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--accent-strong)", marginBottom: 10 }}>{f.k}</div>
              <h3 style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 19, margin: "0 0 8px", color: "var(--green-900)" }}>{f.title}</h3>
              <p style={{ fontSize: 14, lineHeight: 1.55, color: "var(--text-muted)", margin: 0 }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PLATFORM */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "40px 32px 56px" }}>
        <div style={{ maxWidth: "56ch", marginBottom: 30 }}>
          <SectionEyebrow label={t.platKicker} />
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 32, lineHeight: 1.15, letterSpacing: "-0.015em", margin: "16px 0 0", color: "var(--green-900)" }}>{t.platH2}</h2>
          <p style={{ fontSize: 16, color: "var(--text-muted)", margin: "14px 0 0" }}>{t.platSub}</p>
        </div>
        <div className="ds-3col" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
          {t.platform.map((p) => (
            <Link key={p.title} href={p.href} className="card-link" style={{ display: "flex", flexDirection: "column", gap: 4, background: "var(--surface-card)", border: "1px solid var(--border)", borderRadius: 12, padding: "20px 22px", textDecoration: "none", color: "inherit" }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: "var(--green-900)" }}>{p.title}</span>
              <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{p.sub}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* SECURITY STRIP */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "0 32px 16px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "14px 36px", padding: "22px 26px", border: "1px dashed var(--border)", borderRadius: 12 }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--accent-strong)" }}>{t.secKicker}</span>
          {t.security.map((s) => (
            <span key={s} style={{ fontSize: 14, color: "var(--text-body)" }}>{s}</span>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section style={{ maxWidth: 820, margin: "0 auto", padding: "64px 32px" }}>
        <div style={{ display: "flex", justifyContent: "center" }}><SectionEyebrow label={t.faqKicker} center /></div>
        <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 600, fontSize: 36, lineHeight: 1.15, letterSpacing: "-0.015em", margin: "16px 0 32px", color: "var(--green-900)", textAlign: "center" }}>{t.faqH2}</h2>
        <div>
          {t.faqs.map((f, i) => <FaqItem key={f.q} q={f.q} a={f.a} defaultOpen={i === 0} />)}
        </div>
      </section>

      {/* CTA BAND — matches /features and /pricing dark CTA band exactly */}
      <section style={{ padding: "0 32px 96px" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", background: "var(--green-900)", borderRadius: 26, padding: "clamp(48px,7vw,80px) 32px", textAlign: "center", position: "relative", overflow: "hidden" }}>
          <img src="/svendetid-logo.png" alt="" style={{ width: 56, height: 56, margin: "0 auto 18px", display: "block", opacity: 0.96 }} />
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#F4C8A6" }}>{t.bandKicker}</span>
          <h2 style={{ fontFamily: "var(--font-serif)", fontWeight: 700, fontSize: 40, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "18px auto 0", maxWidth: "18em", color: "#fff" }}>{t.bandH2}</h2>
          <p style={{ fontSize: 17, color: "var(--text-hero-muted)", margin: "16px auto 0", maxWidth: "46ch" }}>{t.bandBody}</p>
          <div className="ds-cta-row" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginTop: 34 }}>
            <SignUpButton mode="modal" fallbackRedirectUrl="/org/create"><button className="btn-blue" style={{ cursor: "pointer", border: "none", display: "inline-flex", alignItems: "center", gap: 8, background: "var(--blue)", color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 26px", borderRadius: 11, boxShadow: "0 10px 24px rgba(37,99,235,0.34)" }}>{t.cta}<Icon name="arrow-forward" size={18} /></button></SignUpButton>
            <Link href={pricingHref} className="btn-ghost-dark" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8, color: "#fff", fontSize: 16, fontWeight: 600, padding: "15px 24px", borderRadius: 11, border: "1px solid rgba(255,255,255,0.24)" }}>{t.seePricing}</Link>
          </div>
        </div>
      </section>

      <Footer lang={lang} daHref="/features/automation" enHref="/en/features/automation" />
    </div>
  );
}
